import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import {
  CommandDialog,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
} from "@pos/ui";
import { Clock, Star, Newspaper, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";

// ---------------------------------------------------------------------------
// Recent / frequent tracking in localStorage
// ---------------------------------------------------------------------------

interface NavRecord {
  path: string;
  label: string;
  count: number;
  lastUsed: number;
}

const STORAGE_KEY = "pos-nav-history";
const MAX_RECORDS = 30;

function getNavHistory(): NavRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as NavRecord[]) : [];
  } catch {
    return [];
  }
}

function trackNavigation(path: string, label: string) {
  const history = getNavHistory();
  const existing = history.find((r) => r.path === path);
  if (existing) {
    existing.count++;
    existing.lastUsed = Date.now();
    existing.label = label;
  } else {
    history.push({ path, label, count: 1, lastUsed: Date.now() });
  }
  // Keep only the most recent records
  history.sort((a, b) => b.lastUsed - a.lastUsed);
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify(history.slice(0, MAX_RECORDS)),
  );
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CommandPaletteItem {
  path: string;
  label: string;
  icon: LucideIcon;
  group: string;
}

export interface CommandPaletteReport {
  key: string;
  name: string;
  group: string;
}

interface CommandPaletteProps {
  items: CommandPaletteItem[];
  reports?: CommandPaletteReport[];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CommandPalette({ items, reports = [] }: CommandPaletteProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Cmd+K / Ctrl+K to open
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Track navigation on route change
  useEffect(() => {
    const item = items.find((i) => i.path === location.pathname);
    if (item) {
      trackNavigation(item.path, item.label);
    }
  }, [location.pathname, items]);

  // Recent items (last 5 visited)
  const recentItems = useMemo(() => {
    const history = getNavHistory();
    return history
      .sort((a, b) => b.lastUsed - a.lastUsed)
      .slice(0, 5)
      .map((r) => {
        const item = items.find((i) => i.path === r.path);
        return item ? { ...item, count: r.count } : null;
      })
      .filter(Boolean) as (CommandPaletteItem & { count: number })[];
  }, [items, open]); // recalculate when palette opens

  // Frequent items (top 5 by count, excluding recents)
  const frequentItems = useMemo(() => {
    const history = getNavHistory();
    const recentPaths = new Set(recentItems.map((r) => r.path));
    return history
      .filter((r) => !recentPaths.has(r.path) && r.count > 2)
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
      .map((r) => {
        const item = items.find((i) => i.path === r.path);
        return item ? { ...item, count: r.count } : null;
      })
      .filter(Boolean) as (CommandPaletteItem & { count: number })[];
  }, [items, recentItems, open]);

  // Group all items by category
  const groupedItems = useMemo(() => {
    const groups = new Map<string, CommandPaletteItem[]>();
    for (const item of items) {
      const group = item.group || "Other";
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)!.push(item);
    }
    return [...groups.entries()];
  }, [items]);

  const handleSelect = useCallback(
    (path: string) => {
      const item = items.find((i) => i.path === path);
      if (item) {
        trackNavigation(path, item.label);
      }
      setOpen(false);
      navigate(path);
    },
    [items, navigate],
  );

  const [search, setSearch] = useState("");

  // Reset search when dialog opens
  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen} className="sm:max-w-2xl">
      <Command>
        <div className="relative">
          <CommandInput
            placeholder="Search modules..."
            value={search}
            onValueChange={setSearch}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute top-1/2 right-3 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <CommandList className="max-h-80">
          <CommandEmpty>No modules found.</CommandEmpty>

          {/* Recent */}
          {recentItems.length > 0 && (
            <CommandGroup heading="Recent">
              {recentItems.map((item) => (
                <CommandItem
                  key={`recent-${item.path}`}
                  value={`${item.label} ${item.path}`}
                  onSelect={() => handleSelect(item.path)}
                >
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span>{item.label}</span>
                  <CommandShortcut>↵</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          )}

          {/* Frequent */}
          {frequentItems.length > 0 && (
            <>
              <CommandSeparator />
              <CommandGroup heading="Frequently Used">
                {frequentItems.map((item) => (
                  <CommandItem
                    key={`freq-${item.path}`}
                    value={`${item.label} ${item.path}`}
                    onSelect={() => handleSelect(item.path)}
                  >
                    <Star className="h-4 w-4 text-muted-foreground" />
                    <span>{item.label}</span>
                    <CommandShortcut>↵</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}

          <CommandSeparator />

          {/* All modules by category */}
          {groupedItems.map(([group, groupItems]) => (
            <CommandGroup key={group} heading={group}>
              {groupItems.map((item) => {
                const Icon = item.icon;
                return (
                  <CommandItem
                    key={item.path}
                    value={`${item.label} ${item.group} ${item.path}`}
                    onSelect={() => handleSelect(item.path)}
                  >
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span>{item.label}</span>
                    <CommandShortcut>↵</CommandShortcut>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          ))}
          {/* Reports grouped by category */}
          {reports.length > 0 &&
            (() => {
              const reportGroups = new Map<string, typeof reports>();
              for (const r of reports) {
                const group = r.group || "Reports";
                if (!reportGroups.has(group)) reportGroups.set(group, []);
                reportGroups.get(group)!.push(r);
              }
              return [...reportGroups.entries()].map(
                ([group, groupReports]) => (
                  <CommandGroup key={`rg-${group}`} heading={group}>
                    {groupReports.map((report) => (
                      <CommandItem
                        key={`report-${report.key}`}
                        value={`${report.name} ${report.group} report`}
                        onSelect={() => {
                          setOpen(false);
                          navigate(
                            `/reports?report=${encodeURIComponent(report.key)}`,
                          );
                        }}
                      >
                        <Newspaper className="h-4 w-4 text-muted-foreground" />
                        <span>{report.name}</span>
                        <CommandShortcut>↵</CommandShortcut>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ),
              );
            })()}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
